import { useState } from 'react';
import { FileText, CheckCircle, XCircle, Clock, Eye, Download, AlertTriangle, Trash2, ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';
import { toast } from 'sonner';

const DOCUMENTS_REQUIS = [
  // Documents d'inscription (suivent candidat -> stagiaire)
  { id: 'cni_passeport', nom: "Carte d'identit\u00e9 / Passeport", obligatoire: true, phase: 'inscription' },
  { id: 'psc', nom: 'PSC', obligatoire: true, phase: 'inscription' },
  { id: 'certificat_medical', nom: 'Certificat m\u00e9dical', obligatoire: true, phase: 'inscription' },
  { id: 'photo_identite', nom: "Photo d'identit\u00e9 (format JPG)", obligatoire: true, phase: 'inscription' },
  { id: 'attestation_natation', nom: 'Attestation natation 100m', obligatoire: true, phase: 'inscription' },
  { id: 'attestation_recensement', nom: 'Attestation de recensement', obligatoire: false, phase: 'inscription' },
  { id: 'permis_cotier', nom: 'Permis c\u00f4tier', obligatoire: true, phase: 'inscription' },
  { id: 'permis_fluvial', nom: 'Permis fluvial', obligatoire: true, phase: 'inscription' },
  { id: 'crr', nom: 'CRR', obligatoire: false, phase: 'inscription' },
  { id: 'signature', nom: 'Signature', obligatoire: true, phase: 'inscription' },
  { id: 'cv', nom: 'CV', obligatoire: true, phase: 'inscription' },
  // Documents en cours de formation
  { id: 'attestation_epmsp', nom: 'Attestation EPMSP', obligatoire: false, phase: 'formation' },
  { id: 'declaration_epmsp', nom: 'D\u00e9claration stagiaire', obligatoire: false, phase: 'formation' },
];

const StagiaireDocumentsSection = ({ documents, candidatDocuments, stagiaire, readOnly, onRefresh }) => {
  const [refusMotif, setRefusMotif] = useState({});
  const [showRefusInput, setShowRefusInput] = useState({});
  const [processing, setProcessing] = useState({});

  // Fusionner les documents: candidat + stagiaire
  const allDocs = [...(candidatDocuments || []), ...(documents || [])];

  const getDocForType = (typeId) => allDocs.find(d => d.type_document === typeId || d.document_type === typeId || d.type === typeId);

  const docsManquants = DOCUMENTS_REQUIS.filter(d => d.obligatoire && !getDocForType(d.id));

  const handleView = async (doc) => {
    try {
      toast.info('Chargement du document...');
      const docId = doc.document_id;
      const response = await axios.get(`/documents/download/${docId}`);
      const data = response.data;
      if (data.file_data) {
        const byteCharacters = atob(data.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.content_type || 'application/pdf' });
        window.open(window.URL.createObjectURL(blob), '_blank');
      } else {
        toast.error('Document non disponible');
      }
    } catch {
      // Fallback: try candidat doc-view endpoint
      try {
        const docId = doc.document_id;
        const res = await axios.get(`/candidats/doc-view/${docId}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers['content-type'] }));
        window.open(url, '_blank');
      } catch {
        toast.error('Impossible de charger le document');
      }
    }
  };

  const handleDownload = async (doc) => {
    try {
      toast.info('T\u00e9l\u00e9chargement...');
      const docId = doc.document_id;
      const response = await axios.get(`/documents/download/${docId}`);
      const data = response.data;
      if (data.file_data) {
        const byteCharacters = atob(data.file_data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: data.content_type || 'application/octet-stream' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.nom_fichier || doc.filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      }
    } catch {
      try {
        const res = await axios.get(`/candidats/doc-view/${doc.document_id}`, { responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', doc.nom_fichier || doc.filename || 'document');
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch {
        toast.error('Impossible de t\u00e9l\u00e9charger');
      }
    }
  };

  const handleValidate = async (docId, statut, motif) => {
    setProcessing(prev => ({ ...prev, [docId]: true }));
    try {
      await axios.post(`/documents/${docId}/validate`, {
        statut,
        motif_refus: motif || undefined
      });
      toast.success(statut === 'accepte' ? 'Document accept\u00e9' : 'Document refus\u00e9');
      setShowRefusInput(prev => ({ ...prev, [docId]: false }));
      setRefusMotif(prev => ({ ...prev, [docId]: '' }));
      if (onRefresh) onRefresh();
    } catch {
      toast.error('Erreur lors de la validation');
    } finally {
      setProcessing(prev => ({ ...prev, [docId]: false }));
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    setProcessing(prev => ({ ...prev, [docId]: true }));
    try {
      await axios.delete(`/documents/${docId}`);
      toast.success('Document supprim\u00e9');
      if (onRefresh) onRefresh();
    } catch {
      toast.error('Erreur lors de la suppression');
    } finally {
      setProcessing(prev => ({ ...prev, [docId]: false }));
    }
  };

  const docsInscription = DOCUMENTS_REQUIS.filter(d => d.phase === 'inscription');
  const docsFormation = DOCUMENTS_REQUIS.filter(d => d.phase === 'formation');
  
  // Documents qui ne correspondent à aucun type requis
  const knownTypes = DOCUMENTS_REQUIS.map(d => d.id);
  const autresDocuments = allDocs.filter(d => {
    const type = d.type_document || d.document_type || d.type || '';
    return !knownTypes.includes(type);
  });

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="documents-stagiaire-section">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#8DC63F]" />
          Documents du Stagiaire
        </h2>
        {docsManquants.length > 0 ? (
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-red-100 text-red-700">{docsManquants.length} manquant(s)</span>
        ) : (
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">Complet</span>
        )}
      </div>

      {/* Documents d'inscription */}
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Documents d'inscription</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        {docsInscription.map((docReq) => {
          const uploaded = getDocForType(docReq.id);
          return (
            <DocCard
              key={docReq.id}
              docReq={docReq}
              uploaded={uploaded}
              readOnly={readOnly}
              onView={handleView}
              onDownload={handleDownload}
              onValidate={handleValidate}
              onDelete={handleDelete}
              processing={processing}
              refusMotif={refusMotif}
              setRefusMotif={setRefusMotif}
              showRefusInput={showRefusInput}
              setShowRefusInput={setShowRefusInput}
            />
          );
        })}
      </div>

      {/* Documents en cours de formation */}
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">En cours de formation</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {docsFormation.map((docReq) => {
          const uploaded = getDocForType(docReq.id);
          return (
            <DocCard
              key={docReq.id}
              docReq={docReq}
              uploaded={uploaded}
              readOnly={readOnly}
              onView={handleView}
              onDownload={handleDownload}
              onValidate={handleValidate}
              onDelete={handleDelete}
              processing={processing}
              refusMotif={refusMotif}
              setRefusMotif={setRefusMotif}
              showRefusInput={showRefusInput}
              setShowRefusInput={setShowRefusInput}
            />
          );
        })}
      </div>

      {/* Autres documents (type "autre" ou non classifié) */}
      {autresDocuments.length > 0 && (
        <>
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 mt-6">Autres documents ({autresDocuments.length})</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {autresDocuments.map((doc) => {
              const docId = doc.document_id;
              const filename = doc.filename || doc.nom_fichier || 'Document';
              const date = doc.uploaded_at || doc.date_upload || '';
              const isProcessing = processing[docId];
              return (
                <div key={docId} className="flex items-center justify-between p-3 rounded-lg border border-slate-200 bg-slate-50" data-testid={`other-doc-${docId}`}>
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-slate-700 truncate">{filename}</p>
                      {date && <p className="text-[10px] text-slate-400">{new Date(date).toLocaleDateString('fr-FR')}</p>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => handleView(doc)} disabled={isProcessing} data-testid={`view-other-${docId}`}>
                      <Eye className="h-3 w-3 mr-1" /> Voir
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => handleDownload(docId, filename)} disabled={isProcessing}>
                      <Download className="h-3 w-3" />
                    </Button>
                    {!readOnly && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={() => handleDelete(docId)} disabled={isProcessing}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {docsManquants.length > 0 && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm font-medium text-red-700 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" />
            Documents obligatoires manquants :
          </p>
          <p className="text-xs text-red-600 mt-1 ml-6">
            {docsManquants.map(d => d.nom).join(', ')}
          </p>
        </div>
      )}
    </div>
  );
};

const DocCard = ({ docReq, uploaded, readOnly, onView, onDownload, onValidate, onDelete, processing, refusMotif, setRefusMotif, showRefusInput, setShowRefusInput }) => {
  const statut = uploaded?.statut;
  const docId = uploaded?.document_id;
  const isProcessing = docId && processing[docId];

  const bgClass = statut === 'accepte' ? 'bg-green-50 border-green-200' :
                  statut === 'refuse' ? 'bg-red-50 border-red-200' :
                  uploaded ? 'bg-amber-50 border-amber-200' :
                  'bg-slate-50 border-slate-200';

  return (
    <div className={`p-3 rounded-lg border ${bgClass}`} data-testid={`doc-row-${docReq.id}`}>
      {/* Header: nom + statut */}
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 min-w-0">
          {statut === 'accepte' ? (
            <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
          ) : statut === 'refuse' ? (
            <XCircle className="h-4 w-4 text-red-500 shrink-0" />
          ) : uploaded ? (
            <Clock className="h-4 w-4 text-amber-500 shrink-0" />
          ) : (
            <AlertTriangle className="h-4 w-4 text-slate-400 shrink-0" />
          )}
          <span className="text-sm font-medium text-slate-700 truncate">
            {docReq.nom}
            {docReq.obligatoire && <span className="text-red-400 ml-0.5">*</span>}
          </span>
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${
          statut === 'accepte' ? 'bg-green-100 text-green-700' :
          statut === 'refuse' ? 'bg-red-100 text-red-700' :
          uploaded ? 'bg-amber-100 text-amber-700' :
          'bg-slate-100 text-slate-500'
        }`}>
          {statut === 'accepte' ? 'Accept\u00e9' :
           statut === 'refuse' ? 'Refus\u00e9' :
           uploaded ? 'En attente' :
           'Manquant'}
        </span>
      </div>

      {/* Motif de refus affiché */}
      {statut === 'refuse' && uploaded?.motif_refus && (
        <p className="text-xs text-red-600 ml-6 mb-1 italic">Motif : {uploaded.motif_refus}</p>
      )}

      {/* Actions pour documents présents */}
      {uploaded && docId && (
        <div className="ml-6 mt-2 space-y-2">
          {/* Fichier info */}
          <p className="text-xs text-slate-500 truncate">
            {uploaded.nom_fichier || uploaded.filename || docReq.nom}
            {uploaded.uploaded_at && ` - ${new Date(uploaded.uploaded_at || uploaded.date_upload).toLocaleDateString('fr-FR')}`}
          </p>

          {/* Voir / Télécharger / Supprimer */}
          <div className="flex items-center gap-1 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onView(uploaded)}
              data-testid={`view-doc-${docReq.id}`}
            >
              <Eye className="h-3 w-3 mr-1" /> Voir
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => onDownload(uploaded)}
              data-testid={`download-doc-${docReq.id}`}
            >
              <Download className="h-3 w-3 mr-1" /> {`T\u00e9l\u00e9charger`}
            </Button>

            {!readOnly && (
              <>
                {/* Accepter */}
                {statut !== 'accepte' && (
                  <Button
                    size="sm"
                    className="h-7 px-2 text-xs bg-green-600 hover:bg-green-700 text-white"
                    onClick={() => onValidate(docId, 'accepte')}
                    disabled={isProcessing}
                    data-testid={`accept-doc-${docReq.id}`}
                  >
                    <ThumbsUp className="h-3 w-3 mr-1" /> Accepter
                  </Button>
                )}

                {/* Refuser */}
                {statut !== 'refuse' && !showRefusInput[docId] && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-xs text-orange-600 border-orange-300 hover:bg-orange-50"
                    onClick={() => setShowRefusInput(prev => ({ ...prev, [docId]: true }))}
                    disabled={isProcessing}
                    data-testid={`refuse-doc-btn-${docReq.id}`}
                  >
                    <ThumbsDown className="h-3 w-3 mr-1" /> Refuser
                  </Button>
                )}

                {/* Supprimer */}
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => onDelete(docId)}
                  disabled={isProcessing}
                  data-testid={`delete-doc-${docReq.id}`}
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Supprimer
                </Button>
              </>
            )}
          </div>

          {/* Input motif de refus */}
          {showRefusInput[docId] && (
            <div className="flex items-center gap-2 mt-1">
              <input
                type="text"
                placeholder="Motif du refus..."
                value={refusMotif[docId] || ''}
                onChange={(e) => setRefusMotif(prev => ({ ...prev, [docId]: e.target.value }))}
                className="flex-1 h-7 px-2 text-xs border border-slate-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-400"
                data-testid={`refuse-motif-${docReq.id}`}
              />
              <Button
                size="sm"
                className="h-7 px-2 text-xs bg-orange-500 hover:bg-orange-600 text-white"
                onClick={() => onValidate(docId, 'refuse', refusMotif[docId])}
                disabled={isProcessing || !refusMotif[docId]?.trim()}
                data-testid={`refuse-confirm-${docReq.id}`}
              >
                Confirmer
              </Button>
              <Button
                size="sm"
                variant="outline"
                className="h-7 px-2 text-xs"
                onClick={() => {
                  setShowRefusInput(prev => ({ ...prev, [docId]: false }));
                  setRefusMotif(prev => ({ ...prev, [docId]: '' }));
                }}
              >
                Annuler
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Message pour documents manquants */}
      {!uploaded && (
        <p className="text-xs text-slate-400 ml-6 mt-1 italic">Non fourni</p>
      )}
    </div>
  );
};

export { DOCUMENTS_REQUIS };
export default StagiaireDocumentsSection;
